import React, { Component } from "react";
import { fetchExitData, fetchMeta } from "../../services/exitAnalyzerService";
import "./B10CEAnalyzer.css";

// Color mapping per opening-type group
const GROUP_COLORS = {
  GN: { border: "#0f3460", badge: "#0f3460" },
  GD: { border: "#8b0000", badge: "#8b0000" },
  SW: { border: "#006400", badge: "#006400" },
  GU: { border: "#8b8b00", badge: "#8b8b00" },
  "??": { border: "#555", badge: "#555" },
};
const GROUP_LABELS = { GN: "General", GD: "Gap Down", SW: "SideWay", GU: "Gap Up", "??": "Other" };

// Categorize exitRuleData keys for the attribute panel
const ATTR_CATEGORIES = [
  { label: "IV", keys: ["ivCeH","ivCeL","ivPeH","ivPeL","ivCe","ivPe","ivDiff","tradeIvDiff","candleBarIv","ivRule"] },
  { label: "Delta", keys: ["tradeDeltaDiff","tradeDeltaDiffL","deltaCe","deltaPe","candleBarDelta","deltaExitRule"] },
  { label: "Vega", keys: ["tradeVegaDiff","tradeVegaDiffL","vegaCe","vegaPe","vegaDiff"] },
  { label: "PCR", keys: ["currentPCR","tradePCR","pcrChange","tradePcrChange","currentStrikePricePcr","pcrRule","max_15_minute_atm_pcr","min_15_minute_atm_pcr","max_30_minute_atm_pcr","min_30_minute_atm_pcr","is15MinMaxAndMinPcrStrikePriceSame","is30MinMaxAndMinPcrStrikePriceSame"] },
  { label: "Theta", keys: ["thetaCe","thetaPe","thetaDistance","thetaDistanceExitRule","isThetaBothSidePositive","isThetaBothSideNegative","theta_exit_duration"] },
  { label: "VD", keys: ["vdCe","vdPe","vdDistance","vdDistance10ExitRule","vdDistance20ExitRule","vdDistance30ExitRule","vdDistance60ExitRule","isVdBothSidePositive","isVdBothSideNegative","vd10_exit_duration","vd30_exit_duration","maxVdCe","minVdCe","maxVdPe","minVdPe","vdCeMaxDay","vdPeMaxDay","ceVDExit","ceVDEntry","peVDEntry","peVDExit","ceVDEntryDuration","peVDEntryDuration","ceVDExitDuration","peVDExitDuration"] },
  { label: "OPV", keys: ["opvCe","opvPe","opvDistance","opvDistance10ExitRule","opvDistance20ExitRule","opvDistance30ExitRule","opvDistance60ExitRule","isOpvBothSidePositive","isOpvBothSideNegative","opv10_exit_duration","opv30_exit_duration"] },
  { label: "OI", keys: ["oiDistance","oiExitRule","oiChangeFromMax","oiChangeFromMin","oiChangeMaxFromFirstCandle"] },
  { label: "Gap", keys: ["gapCe","gapPe","gapVdCe","gapVdPe","gapCeP","gapPeP","gapVdCeP","gapVdPeP"] },
  { label: "Trend", keys: ["trendDuration","trendCount","ceTrendCount","peTrendCount"] },
  { label: "Signal", keys: ["strongExit","strong_exit_duration","isLastTradeRoll","isLastExitRoll","rollCount"] },
  { label: "Price", keys: ["closeCe","closePe","strikeCe","strikePe","currentIndexPrice","indexClose","indexEma60","maxIndexPrice","minIndexPrice"] },
  { label: "Time", keys: ["tradeDuration","marketStartTimeInMin","leftDurationInMinute","candleBarHour"] },
  { label: "30m Chg", keys: ["last30MinuteOpvDistanceChangeFromMax","last30MinuteVdDistanceChangeFromMax","last30MinuteOiChangeFromMax","last30MinuteThetaCeChangeFromMax","last30MinuteOpvPeChangeFromMax","last30MinuteVDCeChangeFromMax"] },
  { label: "MaxPain", keys: ["maxPainTrend","maxPainDistance","maxPainStrike"] },
  { label: "Context", keys: ["entryType","indexOpeningType"] },
];

/**
 * Parse exitInfo string to extract group, family, and conditions.
 * Format: "CE_NE_GN_01_01::GN|STR|tradeDelta<=0|oiDist<0|...|FORCE_PUT_ENTRY"
 */
function parseExitInfo(exitInfo) {
  if (!exitInfo) return { group: "??", family: "??", conditions: [] };
  const parts = exitInfo.split("::");
  if (parts.length < 2) return { group: "??", family: "??", conditions: [exitInfo] };
  const segments = parts[1].split("|");
  const group = segments[0] || "??";
  const family = segments[1] || "??";
  // Filter out action markers like FORCE_PUT_ENTRY, isPutITMSell::true
  const conditions = segments.slice(2).filter(
    (s) => !s.startsWith("FORCE_") && !s.startsWith("isPut") && !s.startsWith("isPe") && s.length > 0
  );
  return { group, family, conditions };
}

/**
 * Format a value for display
 */
function formatValue(val) {
  if (val === null || val === undefined) return "-";
  if (typeof val === "boolean") return val ? "TRUE" : "FALSE";
  if (typeof val === "number") {
    if (Number.isInteger(val)) return val.toString();
    return val.toFixed(2);
  }
  if (typeof val === "object") return JSON.stringify(val).substring(0, 50);
  return String(val);
}

function getValueClass(val) {
  if (typeof val === "boolean") return val ? "val-true" : "val-false";
  if (typeof val === "number") {
    if (val > 0) return "val-positive";
    if (val < 0) return "val-negative";
  }
  return "";
}

class B10CEAnalyzer extends Component {
  constructor(props) {
    super(props);
    const today = new Date().toISOString().split("T")[0];
    this.state = {
      // Filters
      indexName: "NIFTY 50",
      optionType: "CE",
      exitClassName: "", // mandatory class filter: B10CE, DirectionCE, StopLossCE, etc.
      selectedDate: today,
      selectedExpiry: "",
      isExpiry: null, // null = all, true = expiry day, false = non-expiry
      selectedTime: "", // HH:mm format, empty = show all times
      // Data
      exitItems: [],
      availableExpiries: [],
      availableDates: [],
      availableClassNames: [],
      // UI state
      hoveredRule: null,
      sortBy: "time",
      sortDir: "desc",
      searchText: "",
      selectedGroup: "ALL",
      loading: false,
      error: null,
      metaLoading: false,
    };
  }

  componentDidMount() {
    this.loadMeta();
    this.loadData();
  }

  loadMeta = async () => {
    this.setState({ metaLoading: true });
    try {
      const meta = await fetchMeta({
        indexName: this.state.indexName,
        optionType: this.state.optionType,
      });
      const parsed = typeof meta === "string" ? JSON.parse(meta) : meta;
      this.setState({
        availableExpiries: parsed.expiries || [],
        availableDates: parsed.dates || [],
        availableClassNames: parsed.exitClassNames || [],
        metaLoading: false,
      });
    } catch (err) {
      console.error("Failed to load meta:", err);
      this.setState({ metaLoading: false });
    }
  };

  loadData = async () => {
    const { indexName, optionType, selectedDate, selectedExpiry, isExpiry } = this.state;
    if (!selectedDate) return;
    this.setState({ loading: true, error: null });
    try {
      const data = await fetchExitData({
        indexName,
        optionType,
        onlyDate: selectedDate,
        expiry: selectedExpiry || undefined,
        isExpiry: isExpiry,
      });
      const parsed = typeof data === "string" ? JSON.parse(data) : data;
      const items = Array.isArray(parsed) ? parsed : [];
      // Extract unique class names from fetched data for the Class dropdown
      const classNamesFromData = [...new Set(items.map(i => i.exitClassName).filter(Boolean))].sort();
      this.setState({
        exitItems: items,
        loading: false,
        availableClassNames: classNamesFromData,
      });
    } catch (err) {
      console.error("Failed to load exit data:", err);
      this.setState({ error: err.response?.data?.error || err.message, loading: false, exitItems: [] });
    }
  };

  onFilterChange = (key, value, alsoReloadMeta = false) => {
    const updates = { [key]: value };
    // Reset exitClassName when index or optionType changes (class list will differ)
    if (key === "indexName" || key === "optionType") {
      updates.exitClassName = "";
    }
    this.setState(updates, () => {
      if (alsoReloadMeta) this.loadMeta();
      this.loadData();
    });
  };

  adjustDate = (days) => {
    const d = new Date(this.state.selectedDate);
    d.setDate(d.getDate() + days);
    this.setState({ selectedDate: d.toISOString().split("T")[0] }, this.loadData);
  };

  adjustTime = (mins) => {
    const { selectedTime } = this.state;
    if (!selectedTime) {
      // Default to 09:15 if no time set
      this.setState({ selectedTime: "09:15" });
      return;
    }
    const [h, m] = selectedTime.split(":").map(Number);
    let totalMin = h * 60 + m + mins;
    if (totalMin < 555) totalMin = 555; // 9:15
    if (totalMin > 930) totalMin = 930; // 15:30
    const hh = String(Math.floor(totalMin / 60)).padStart(2, "0");
    const mm = String(totalMin % 60).padStart(2, "0");
    this.setState({ selectedTime: `${hh}:${mm}` });
  };

  getProcessedItems = () => {
    const { exitItems, searchText, selectedGroup, selectedTime, exitClassName } = this.state;
    let items = exitItems.map((item) => {
      const parsed = parseExitInfo(item.exitInfo);
      return { ...item, _group: parsed.group, _family: parsed.family, _conditions: parsed.conditions };
    });
    // Client-side filter by exitClassName (works even if backend doesn't support it)
    if (exitClassName) {
      items = items.filter((i) => i.exitClassName === exitClassName);
    }
    // Time filter — show exits that fired AT OR BEFORE the selected time
    if (selectedTime) {
      items = items.filter((i) => {
        if (!i.onlyTime) return false;
        const exitTime = i.onlyTime.substring(0, 5); // HH:mm
        return exitTime <= selectedTime;
      });
    }
    if (selectedGroup !== "ALL") {
      items = items.filter((i) => i._group === selectedGroup);
    }
    if (searchText) {
      const q = searchText.toLowerCase();
      items = items.filter(
        (i) =>
          (i.exitUniqueCode || "").toLowerCase().includes(q) ||
          (i.exitInfo || "").toLowerCase().includes(q) ||
          (i.exitCode || "").toLowerCase().includes(q)
      );
    }
    return items;
  };

  getSortedItems = () => {
    const items = this.getProcessedItems();
    const { sortBy, sortDir } = this.state;
    const dir = sortDir === "asc" ? 1 : -1;
    return items.sort((a, b) => {
      if (sortBy === "time") return ((a.tradeDateTimeMilli || 0) - (b.tradeDateTimeMilli || 0)) * dir;
      if (sortBy === "rule") return (a.exitUniqueCode || "").localeCompare(b.exitUniqueCode || "") * dir;
      if (sortBy === "conditions") return ((a._conditions?.length || 0) - (b._conditions?.length || 0)) * dir;
      return 0;
    });
  };

  toggleSort = (col) => {
    this.setState((prev) => ({
      sortBy: col,
      sortDir: prev.sortBy === col && prev.sortDir === "desc" ? "asc" : "desc",
    }));
  };

  renderConditionDots = (conditions) => {
    if (!conditions || conditions.length === 0) return null;
    // Since these exits actually fired, all conditions were met = all green
    return (
      <div className="condition-dots">
        {conditions.map((c, i) => (
          <span key={i} className="dot pass" title={c} />
        ))}
      </div>
    );
  };

  renderDetailPanel = (item, colors) => {
    const data = item.exitRuleData || {};
    return (
      <tr className="detail-row" key={item.id + "_detail"}>
        <td colSpan={9}>
          <div className="detail-panel" style={{ borderColor: colors.border }}>
            <div className="detail-header">
              <strong>{item.exitUniqueCode}</strong>
              <span className="detail-match">
                {item._conditions?.length || 0} conditions | Expiry: {item.expiry} | Left Days: {item.leftDay}
              </span>
            </div>
            {/* Conditions list */}
            <div className="detail-conditions">
              {(item._conditions || []).map((c, i) => (
                <div key={i} className="detail-cond cond-pass">
                  <span className="cond-icon icon-pass">{"\u2713"}</span>
                  <span className="cond-text">{c}</span>
                </div>
              ))}
            </div>
            {/* exitRuleData categorized grid */}
            <div className="detail-attrs">
              {ATTR_CATEGORIES.map((cat) => {
                const entries = cat.keys.filter((k) => data[k] !== undefined && data[k] !== null);
                if (entries.length === 0) return null;
                return (
                  <div key={cat.label} className="detail-attr-group">
                    <div className="detail-attr-group-label">{cat.label}</div>
                    <div className="detail-attr-items">
                      {entries.map((k) => (
                        <div key={k} className="detail-attr-item">
                          <span className="detail-attr-key">{k}</span>
                          <span className={`detail-attr-val ${getValueClass(data[k])}`}>
                            {formatValue(data[k])}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="detail-meta">
              <span>Entry: <strong>{data.entryType || "-"}</strong></span>
              <span>Opening: <strong>{String(data.indexOpeningType || "-")}</strong></span>
              <span>Duration: <strong>{data.tradeDuration || "-"} min</strong></span>
              <span>Market Time: <strong>{data.marketStartTimeInMin || "-"} min</strong></span>
            </div>
          </div>
        </td>
      </tr>
    );
  };

  render() {
    const {
      indexName, optionType, exitClassName, selectedDate, selectedExpiry, isExpiry,
      availableExpiries, availableClassNames, hoveredRule, sortBy, sortDir,
      searchText, selectedGroup, loading, error,
    } = this.state;

    // Friendly display names for exit class names
    const CLASS_DISPLAY = {
      B10CE: "B10 CE", B10PE: "B10 PE",
      DirectionCE: "Direction CE", DirectionPE: "Direction PE",
      StopLossCE: "StopLoss CE", StopLossPE: "StopLoss PE",
      NonDirectionCE: "NonDirection CE", NonDirectionPE: "NonDirection PE",
      Last60MCE: "Last60M CE", Last60MPE: "Last60M PE",
      RollDownExit: "RollDown", RollUpExit: "RollUp",
      MLExitCE: "ML CE", MLExitPE: "ML PE",
    };

    const sortedItems = this.getSortedItems();
    const allProcessed = this.getProcessedItems();

    // Group stats
    const groupStats = {};
    ["GN", "GD", "SW", "GU"].forEach((g) => {
      groupStats[g] = allProcessed.filter((i) => i._group === g).length;
    });
    const otherCount = allProcessed.filter((i) => !["GN", "GD", "SW", "GU"].includes(i._group)).length;

    // Unique rule count
    const uniqueRules = new Set(allProcessed.map((i) => i.exitUniqueCode)).size;

    return (
      <div className="b10ce-analyzer">
        {/* Header */}
        <div className="analyzer-header">
          <div className="header-title">
            <h2>Exit Rule Analyzer</h2>
            <span className="subtitle">
              {indexName} | {optionType}{exitClassName ? ` | ${CLASS_DISPLAY[exitClassName] || exitClassName}` : ""} | {selectedDate}{this.state.selectedTime ? ` | upto ${this.state.selectedTime}` : ""}
            </span>
          </div>
          <div className="header-stats">
            <div className={`stat-card ${sortedItems.length > 0 ? "has-match" : ""}`}>
              <div className="stat-value">{sortedItems.length}</div>
              <div className="stat-label">Exits Found</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{uniqueRules}</div>
              <div className="stat-label">Unique Rules</div>
            </div>
          </div>
        </div>

        {/* Filters Bar */}
        <div className="filters-bar">
          <div className="filter-group">
            <label>Index</label>
            <select value={indexName} onChange={(e) => this.onFilterChange("indexName", e.target.value, true)}>
              <option value="NIFTY 50">NIFTY 50</option>
              <option value="SENSEX">SENSEX</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Option</label>
            <select value={optionType} onChange={(e) => this.onFilterChange("optionType", e.target.value, true)}>
              <option value="CE">CALL (CE)</option>
              <option value="PE">PUT (PE)</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Class</label>
            <select
              value={exitClassName}
              onChange={(e) => this.onFilterChange("exitClassName", e.target.value)}
              className={exitClassName ? "" : "filter-highlight"}
            >
              <option value="">All Classes</option>
              {availableClassNames.map((cn) => (
                <option key={cn} value={cn}>{CLASS_DISPLAY[cn] || cn}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Date</label>
            <div className="filter-with-buttons">
              <button className="adj-btn" onClick={() => this.adjustDate(-7)}>-7</button>
              <button className="adj-btn" onClick={() => this.adjustDate(-1)}>-1</button>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => this.onFilterChange("selectedDate", e.target.value)}
              />
              <button className="adj-btn" onClick={() => this.adjustDate(1)}>+1</button>
              <button className="adj-btn" onClick={() => this.adjustDate(7)}>+7</button>
            </div>
          </div>
          <div className="filter-group">
            <label>Time</label>
            <div className="filter-with-buttons">
              <button className="adj-btn" onClick={() => this.adjustTime(-5)}>-5m</button>
              <button className="adj-btn" onClick={() => this.adjustTime(-1)}>-1m</button>
              <input
                type="time"
                value={this.state.selectedTime}
                onChange={(e) => this.setState({ selectedTime: e.target.value })}
                placeholder="All"
                className="time-input"
              />
              <button className="adj-btn" onClick={() => this.adjustTime(1)}>+1m</button>
              <button className="adj-btn" onClick={() => this.adjustTime(5)}>+5m</button>
              {this.state.selectedTime && (
                <button className="adj-btn adj-btn-clear" onClick={() => this.setState({ selectedTime: "" })}>All</button>
              )}
            </div>
          </div>
          <div className="filter-group">
            <label>Expiry</label>
            <select value={selectedExpiry} onChange={(e) => this.onFilterChange("selectedExpiry", e.target.value)}>
              <option value="">All Expiries</option>
              {availableExpiries.map((exp) => (
                <option key={exp} value={exp}>{exp}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Day Type</label>
            <select
              value={isExpiry === null ? "all" : isExpiry ? "expiry" : "nonexpiry"}
              onChange={(e) => {
                const val = e.target.value;
                this.onFilterChange("isExpiry", val === "all" ? null : val === "expiry");
              }}
            >
              <option value="all">All</option>
              <option value="expiry">Expiry Day</option>
              <option value="nonexpiry">Non-Expiry</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Opening</label>
            <select
              value={selectedGroup}
              onChange={(e) => this.setState({ selectedGroup: e.target.value })}
            >
              <option value="ALL">All</option>
              <option value="GN">General</option>
              <option value="GD">Gap Down</option>
              <option value="SW">SideWay</option>
              <option value="GU">Gap Up</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Search</label>
            <input
              type="text"
              placeholder="Rule ID, condition..."
              value={searchText}
              onChange={(e) => this.setState({ searchText: e.target.value })}
              className="search-input"
            />
          </div>
        </div>

        {/* Group Summary Cards */}
        <div className="group-summary-row">
          {["GN", "GD", "SW", "GU"].map((g) => (
            <div
              key={g}
              className={`group-card ${selectedGroup === g ? "active" : ""}`}
              style={{ borderColor: GROUP_COLORS[g].border }}
              onClick={() => this.setState({ selectedGroup: selectedGroup === g ? "ALL" : g })}
            >
              <div className="group-card-header" style={{ backgroundColor: GROUP_COLORS[g].badge }}>
                {GROUP_LABELS[g]}
              </div>
              <div className="group-card-body">
                <span className="group-match-count" style={{ color: groupStats[g] > 0 ? "#28a745" : "#888" }}>
                  {groupStats[g]}
                </span>
              </div>
            </div>
          ))}
          {otherCount > 0 && (
            <div
              className={`group-card ${selectedGroup === "??" ? "active" : ""}`}
              style={{ borderColor: "#555" }}
              onClick={() => this.setState({ selectedGroup: selectedGroup === "??" ? "ALL" : "??" })}
            >
              <div className="group-card-header" style={{ backgroundColor: "#555" }}>Other</div>
              <div className="group-card-body">
                <span className="group-match-count" style={{ color: otherCount > 0 ? "#28a745" : "#888" }}>
                  {otherCount}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Loading / Error */}
        {loading && <div className="loading-indicator">Loading exit data...</div>}
        {error && <div className="error-message">Error: {error}</div>}

        {/* Main Table */}
        {!loading && (
          <div className="table-container">
            <table className="exit-table">
              <thead>
                <tr>
                  <th className="th-idx">#</th>
                  <th className="th-id sortable" onClick={() => this.toggleSort("rule")}>
                    Exit Rule {sortBy === "rule" ? (sortDir === "asc" ? "\u25B2" : "\u25BC") : ""}
                  </th>
                  <th className="th-class">Class</th>
                  <th className="th-group">Group</th>
                  <th className="th-family">Family</th>
                  <th className="th-time sortable" onClick={() => this.toggleSort("time")}>
                    Time {sortBy === "time" ? (sortDir === "asc" ? "\u25B2" : "\u25BC") : ""}
                  </th>
                  <th className="th-conds sortable" onClick={() => this.toggleSort("conditions")}>
                    Conditions {sortBy === "conditions" ? (sortDir === "asc" ? "\u25B2" : "\u25BC") : ""}
                  </th>
                  <th className="th-dots">Condition Breakdown</th>
                  <th className="th-status">Expiry</th>
                </tr>
              </thead>
              <tbody>
                {sortedItems.map((item, idx) => {
                  const isHovered = hoveredRule === item.id;
                  const colors = GROUP_COLORS[item._group] || GROUP_COLORS["??"];
                  return (
                    <React.Fragment key={item.id || idx}>
                      <tr
                        className={`exit-row row-active ${isHovered ? "row-hovered" : ""}`}
                        style={{ borderLeft: `4px solid ${colors.border}` }}
                        onMouseEnter={() => this.setState({ hoveredRule: item.id })}
                        onMouseLeave={() => this.setState({ hoveredRule: null })}
                      >
                        <td className="td-idx">{idx + 1}</td>
                        <td className="td-id">
                          <span className="rule-id">{item.exitUniqueCode}</span>
                          <span className="rule-desc">{item.exitCode !== item.exitUniqueCode ? item.exitCode : ""}</span>
                        </td>
                        <td className="td-class">
                          <span className="badge-class">{CLASS_DISPLAY[item.exitClassName] || item.exitClassName || "-"}</span>
                        </td>
                        <td>
                          <span className="badge-group" style={{ backgroundColor: colors.badge }}>
                            {item._group}
                          </span>
                        </td>
                        <td>
                          <span className="badge-family">{item._family}</span>
                        </td>
                        <td className="td-time">{item.onlyTime ? item.onlyTime.substring(0, 8) : "-"}</td>
                        <td className="td-conds">{item._conditions?.length || 0}</td>
                        <td className="td-dots">{this.renderConditionDots(item._conditions)}</td>
                        <td className="td-expiry">
                          <span className={`badge-expiry ${item.leftDay === 0 ? "badge-expiry-day" : ""}`}>
                            {item.expiry || "-"}
                          </span>
                        </td>
                      </tr>
                      {isHovered && this.renderDetailPanel(item, colors)}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            {sortedItems.length === 0 && !loading && (
              <div className="no-results">
                No exit rules found for {indexName} {optionType} on {selectedDate}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
}

export default B10CEAnalyzer;
